import React from 'react';

/**
 * PageSkeleton.jsx
 * 
 * Animated shimmer skeleton displayed while a lazy page chunk is loading.
 * Replaces the blank white "Loading..." screen that appears during
 * code-split chunk downloads.
 */

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -800px 0; }
    100% { background-position:  800px 0; }
  }
`;

const skeletonBase = {
  background: 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)',
  backgroundSize: '800px 100%',
  animation: 'shimmer 1.4s infinite linear',
  borderRadius: '6px',
};

function Bar({ width = '100%', height = 16, mb = 8, style = {} }) {
  return (
    <div
      style={{
        ...skeletonBase,
        width,
        height,
        marginBottom: mb,
        ...style,
      }}
    />
  );
}

function Card({ style = {} }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        padding: '18px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        flex: 1,
        minWidth: 0,
        ...style,
      }}
    >
      <Bar width="55%" height={12} mb={10} />
      <Bar width="80%" height={10} mb={8} />
      <Bar width="65%" height={10} mb={0} />
    </div>
  );
}

export default function PageSkeleton() {
  return (
    <>
      {/* Inject keyframe CSS once */}
      <style>{shimmer}</style>

      <div style={{ padding: '28px 16px', width: '100%' }}>
        {/* Top bar — mimics page title + action button row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <Bar width={200} height={22} mb={0} />
          <Bar width={120} height={34} mb={0} style={{ borderRadius: 8 }} />
        </div>

        {/* Table header row */}
        <div
          style={{
            background: '#fff',
            borderRadius: 10,
            padding: '14px 20px',
            marginBottom: 4,
            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            display: 'flex',
            gap: 16,
          }}
        >
          {[60, 120, 180, 120, 80, 100].map((w, i) => (
            <Bar key={i} width={w} height={10} mb={0} />
          ))}
        </div>

        {/* Table rows */}
        {Array.from({ length: 7 }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            style={{
              background: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
              borderRadius: 6,
              padding: '12px 20px',
              marginBottom: 2,
              display: 'flex',
              gap: 16,
              alignItems: 'center',
            }}
          >
            {[40, 110, 160, 100, 70, 90].map((w, colIdx) => (
              <Bar key={colIdx} width={w} height={10} mb={0} />
            ))}
          </div>
        ))}

        {/* Pagination row */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Bar key={i} width={32} height={32} mb={0} style={{ borderRadius: 6 }} />
          ))}
        </div>
      </div>
    </>
  );
}
